





































































































































































































































































































































































































import { Component, Mixins, Prop, Watch } from 'vue-property-decorator';
import { Button, FormItem, Input, Option, Select } from 'element-ui';
import NullableNumberInput from '@/components/element-ui/NullableNumberInput.vue';
import UtcDatePicker from '@/components/element-ui/UtcDatePicker.vue';

// sections
import HeaderSection from '@/components/documents/sections/Header.vue';
import ContactSection from '@/components/documents/sections/ContactInformation.vue';

// options
import { blTypeOptions } from '@/constants/blTypesOptions';
import { containerSizeOptions } from '@/constants/containerSizeOptions';
import packagingOptions from '@/constants/packagingOptions';
import { paymentTermsOptions } from '@/constants/PaymentTermsOptions';

// mixin
import carriers from '@/mixins/carrierOptions';
import partners from '@/mixins/partnerOptions.ts';
import places from '@/mixins/placeOptions';
import vessels from '@/mixins/vesselOptions';

// types
import {
  BlTypeEnum,
  CurrencyEnum,
  PaymentTermsTypeEnum,
  ShipmentDocumentDataInput,
  ShipmentDocumentFields,
  TransportationCargoTypeEnum
} from '@/types/graphql/schemaTypes';

@Component({
  components: {
    ContactSection,
    HeaderSection,
    NullableNumberInput,
    UtcDatePicker,
    'el-button': Button,
    'el-form-item': FormItem,
    'el-input': Input,
    'el-option': Option,
    'el-select': Select
  }
})
export default class ShippingInstructions extends Mixins(
  carriers,
  partners,
  places,
  vessels
) {
  @Prop() document!: ShipmentDocumentFields;
  @Prop({ default: false, type: Boolean }) isLoading!: boolean;

  formInput: ShipmentDocumentDataInput = {};
  emptyContainer = {
    containerNumber: null,
    sealNumber: null,
    containerType: null,
    containerCount: null
  };

  // select options
  readonly blTypeOptions = blTypeOptions();
  readonly containerSizeOptions = containerSizeOptions();
  readonly packagingOptions = packagingOptions();
  readonly paymentTermsOptions = paymentTermsOptions();
  readonly TransportationCargoTypeEnum = TransportationCargoTypeEnum;

  get containerPackageCount() {
    if (this.document.data.transportCargoType === TransportationCargoTypeEnum.FCL) {
      const mapping = container => {
        // only count container with container type
        if (container.containerType) {
          return container.containerCount || 0;
        }
        return 0;
      };
      const accumulate = (accumulator, currentValue) => {
        return accumulator + currentValue;
      };

      return this.formInput.containerNumbers.map(mapping).reduce(accumulate);
    } else {
      // LCL
      return this.formInput.packagingQuantity || 0;
    }
  }

  get blType() {
    return this.formInput.blType;
  }

  set blType(data: BlTypeEnum | '') {
    this.formInput.blType = data === '' ? null : data;
  }

  get packaging() {
    return this.formInput.packaging;
  }

  set packaging(data: string | '') {
    this.formInput.packaging = data === '' ? null : data;
  }

  get shipmentPaymentTerms() {
    return this.formInput.shipmentPaymentTerms;
  }

  set shipmentPaymentTerms(data: PaymentTermsTypeEnum | '') {
    this.formInput.shipmentPaymentTerms = data === '' ? null : data;
  }

  created() {
    this.setFormInput();
    // disable auto select for place select
    this.autoSelect = false;
    // init transport type for place select
    this.transportationType = this.document.data.transportationType;
  }

  addContainer() {
    this.formInput.containerNumbers.push({ ...this.emptyContainer });
  }

  containerSizeSelected(size: string) {
    return this.formInput.containerNumbers
      .map(container => {
        return container.containerType as string;
      })
      .includes(size);
  }

  deleteContainer(index) {
    this.formInput.containerNumbers.splice(index, 1);
  }

  @Watch('document')
  setFormInput() {
    this.formInput = {
      // IMPORTANT re send readonly data
      shipmentCustomerRole: this.document.data?.shipmentCustomerRole,
      shipmentReference: this.document.data?.shipmentReference,
      transportCargoType: this.document.data?.transportCargoType,
      transportationType: this.document.data?.transportationType,
      // END IMPORTANT

      // Header information
      assignedOperator: this.document.data?.assignedOperator,
      issuanceDate: this.document.data?.issuanceDate,

      // Contact information
      shipmentShipperName: this.document.data?.shipmentShipperName,
      shipmentShipperAddress: this.document.data?.shipmentShipperAddress,
      shipmentShipperEmail: this.document.data?.shipmentShipperEmail,
      shipmentShipperPhone: this.document.data?.shipmentShipperPhone,
      shipmentConsigneeName: this.document.data?.shipmentConsigneeName,
      shipmentConsigneeAddress: this.document.data?.shipmentConsigneeAddress,
      shipmentConsigneeEmail: this.document.data?.shipmentConsigneeEmail,
      shipmentConsigneePhone: this.document.data?.shipmentConsigneePhone,
      shipmentNotifyName: this.document.data?.shipmentNotifyName,
      shipmentNotifyAddress: this.document.data?.shipmentNotifyAddress,
      shipmentNotifyEmail: this.document.data?.shipmentNotifyEmail,
      shipmentNotifyPhone: this.document.data?.shipmentNotifyPhone,
      shipmentAlsoNotifyName: this.document.data?.shipmentAlsoNotifyName,
      shipmentAlsoNotifyAddress: this.document.data?.shipmentAlsoNotifyAddress,
      shipmentAlsoNotifyEmail: this.document.data?.shipmentAlsoNotifyEmail,
      shipmentAlsoNotifyPhone: this.document.data?.shipmentAlsoNotifyPhone,

      // Booking information
      bookingNumber: this.document.data?.bookingNumber,
      consolidator: this.document.data?.consolidator,
      seaCarrier: this.document.data?.seaCarrier,
      estimatedDateOfDeparture: this.document.data?.estimatedDateOfDeparture,
      vesselName: this.document.data?.vesselName,
      voyageNumber: this.document.data?.voyageNumber,
      preCarriageBy: this.document.data?.preCarriageBy,
      placeOfReceipt: this.document.data?.placeOfReceipt,
      portOfLoading: this.document.data?.portOfLoading,
      portOfDischarge: this.document.data?.portOfDischarge,
      placeOfDelivery: this.document.data?.placeOfDelivery,
      finalDestination: this.document.data?.finalDestination,
      packagingQuantity: this.document.data?.packagingQuantity,
      packaging: this.document.data?.packaging,
      grossWeight: this.document.data?.grossWeight,
      grossVolume: this.document.data?.grossVolume,
      remarks: this.document.data?.remarks,

      // Goods informations
      descriptionOfGoods: this.document.data?.descriptionOfGoods,
      shippingMarks: this.document.data?.shippingMarks,
      containerNumbers: this.document.data?.containerNumbers.length
        ? [...this.document.data?.containerNumbers]
        : [{ ...this.emptyContainer }],

      // BL informations
      hsCode: this.document.data?.hsCode,
      blType: this.document.data?.blType,
      shipmentPaymentPlace: this.document.data?.shipmentPaymentPlace,
      blPlaceOfIssuance: this.document.data?.blPlaceOfIssuance,
      shipmentPaymentTerms: this.document.data?.shipmentPaymentTerms,
      blNumberOfOriginals: this.document.data?.blNumberOfOriginals
    };
  }
}
